import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnDestroy, ViewEncapsulation, inject, signal } from '@angular/core';
import { Task, TaskLinkType } from '@jms/model';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { DateTimeFormatPipe } from '@workbuddy/ui/fields/luxon.calendar';
import { CompletionStatusComponent } from '@workbuddy/ui/v2/components/completion.status';
import { HoverCardComponent } from '@workbuddy/ui/v2/components/hover.card';
import { observeVisibleOnce } from '@workbuddy/utils/async';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { SorTaskCountService, watchSorTaskCount } from '../../services/item.task.count.service';
import { resolveItemId } from '@workbuddy/entities/shared/items';
import { ItemCompletion } from './item.completion.card';
import { resolveItemCompletion } from './item.completion.hover';

/**
 * Parent item-task completion icon (CountByItems). Deferred until visible; re-loads on
 * {@link SorTaskCountService} invalidate / optimistic patch without recreating the row.
 */
@Component({
  selector: 'item-completion-cell-renderer',
  standalone: true,
  template: `
    @if ($completion().rows; as rows) {
      <span class="completion-hover" [pTooltip]="cardTemplate" [autoHide]="false" [showDelay]="200" tooltipPosition="top" tooltipStyleClass="wb-dark-tooltip" appendTo="body">
        <wb-completion-status [status]="$completion().state" [size]="16" />
      </span>
      <ng-template #cardTemplate><wb-hover-card [rows]="rows" /></ng-template>
    } @else {
      <wb-completion-status [status]="$completion().state" [size]="16" />
    }
  `,
  styleUrls: ['./item.completion.cell.renderer.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CompletionStatusComponent, HoverCardComponent, TooltipModule],
})
export class ItemCompletionCellRendererComponent implements ICellRendererAngularComp, OnDestroy {

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly taskCounts = inject(SorTaskCountService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly generalSetting = inject(GeneralSettingService);
  private readonly dates = new DateTimeFormatPipe();
  private readonly dateFormat = `${this.generalSetting.generalSettings?.region?.dateFormat ?? 'dd/MM/yyyy'} ${this.generalSetting.generalSettings?.region?.timeFormat ?? 'HH:mm'}`;

  protected readonly $completion = signal<ItemCompletion>({ state: 'pending' });

  private timezone: string | undefined;

  private itemId: string | null = null;
  private visible = false;
  private dispose: (() => void) | null = null;

  public agInit(params: ICellRendererParams & { timezone?: (stageId?: string) => string }): void {

    this.timezone = params.timezone?.(params.data?.workOrders?._id) ?? this.generalSetting.generalSettings?.region?.timezone;
    this.itemId = resolveItemId(params);
    if (!this.itemId) return;

    this.dispose = observeVisibleOnce(this.el.nativeElement, () => {
      this.visible = true;
      void this.load();
    });

    watchSorTaskCount(
      this.taskCounts,
      () => this.itemId,
      () => this.visible,
      () => void this.load(),
      this.destroyRef,
    );
  }

  public refresh(): boolean {

    // Recreate on grid data reload (paired with SorTaskCountService.clear()).
    return false;
  }

  public ngOnDestroy(): void {

    this.dispose?.();
  }

  private async load(): Promise<void> {

    if (!this.itemId) return;

    const count = await this.taskCounts.getCount(this.itemId);
    const parent = count?.tasks?.find((task: Task) => task.type === TaskLinkType.Item);
    this.$completion.set(resolveItemCompletion(parent, value => this.dates.transform(value, this.timezone, this.dateFormat)));
  }
}
